import React, { useState, useEffect } from 'react';
import { 
  BarChart2, TrendingUp, Users, Target, Trophy, RefreshCw, 
  FileText, Gift, Megaphone, Plus, Settings, Eye, MousePointer,
  ChevronRight, FlaskConical, Layers, Edit2, Trash2, Play, Pause, X, Check
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Test Card Component
const TestCard = ({ type, icon, iconBg, iconColor, name, description, variants, isActive, onViewStats, onManage, onToggle }) => {
  const totalImpressions = variants.reduce((sum, v) => sum + (v.impressions || 0), 0);
  const totalConversions = variants.reduce((sum, v) => sum + (v.conversions || 0), 0);
  const overallRate = totalImpressions > 0 ? ((totalConversions / totalImpressions) * 100).toFixed(1) : 0;
  
  return (
    <div className="bg-white rounded-xl border p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className={`p-2 ${iconBg} rounded-lg ${iconColor}`}>
            {icon}
          </div>
          <div>
            <h4 className="font-semibold text-gray-900">{name}</h4>
            <p className="text-sm text-gray-500">{description}</p>
          </div>
        </div>
        <div className={`px-2 py-1 rounded-full text-xs font-medium ${isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
          {isActive ? 'Active' : 'Paused'}
        </div>
      </div>
      
      <div className="mt-4 grid grid-cols-3 gap-4 text-center">
        <div className="bg-gray-50 rounded-lg p-2">
          <p className="text-xs text-gray-500">Variants</p>
          <p className="font-bold text-lg">{variants.length}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-2">
          <p className="text-xs text-gray-500">Impressions</p>
          <p className="font-bold text-lg">{totalImpressions.toLocaleString()}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-2">
          <p className="text-xs text-gray-500">Conv. Rate</p>
          <p className="font-bold text-lg">{overallRate}%</p>
        </div>
      </div>

      {/* Variant breakdown */}
      {variants.length > 0 && (
        <div className="mt-4 space-y-2">
          {variants.slice(0, 3).map((variant, idx) => {
            const rate = variant.impressions > 0 ? ((variant.conversions || 0) / variant.impressions * 100).toFixed(1) : 0;
            const isWinning = variants.length > 1 && rate === Math.max(...variants.map(v => v.impressions > 0 ? (v.conversions || 0) / v.impressions * 100 : 0)).toFixed(1);
            return (
              <div key={variant.id || idx} className={`flex items-center justify-between p-2 rounded ${isWinning ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                <span className="text-sm font-medium">{variant.name || `Variant ${idx + 1}`}</span>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-500">{variant.impressions || 0} views</span>
                  <span className={`font-medium ${isWinning ? 'text-green-600' : 'text-gray-900'}`}>{rate}%</span>
                  {isWinning && <Trophy className="h-4 w-4 text-green-600" />}
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      <div className="mt-4 flex gap-2">
        <Button variant="outline" size="sm" className="flex-1" onClick={onViewStats}>
          <BarChart2 className="h-4 w-4 mr-1" /> Stats
        </Button>
        <Button variant="outline" size="sm" className="flex-1" onClick={onManage}>
          <Settings className="h-4 w-4 mr-1" /> Manage
        </Button>
        <Button variant="outline" size="sm" onClick={onToggle}>
          {isActive ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
};

const ABTestingDashboard = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [landingPages, setLandingPages] = useState([]);
  const [allLandingPages, setAllLandingPages] = useState([]);
  const [banners, setBanners] = useState([]);
  const [allBanners, setAllBanners] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [allCoupons, setAllCoupons] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [testStats, setTestStats] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showManageDialog, setShowManageDialog] = useState(false);
  const [createType, setCreateType] = useState('landing');
  const [manageItem, setManageItem] = useState(null);
  const [manageType, setManageType] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAllTests = async () => {
    try {
      const [landingRes, bannersRes, couponsRes] = await Promise.all([
        axios.get(`${API_URL}/api/landing-pages`, { headers }),
        axios.get(`${API_URL}/api/marketing/banners`, { headers }),
        axios.get(`${API_URL}/api/marketing/coupons`, { headers })
      ]);
      
      setAllLandingPages(landingRes.data || []);
      setAllBanners(bannersRes.data || []);
      setAllCoupons(couponsRes.data || []);
      
      setLandingPages((landingRes.data || []).filter(p => p.ab_testing_enabled));
      setBanners((bannersRes.data || []).filter(b => b.ab_testing_enabled));
      setCoupons((couponsRes.data || []).filter(c => c.ab_testing_enabled));
    } catch (error) {
      console.error('Failed to fetch A/B tests:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAllTests();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAllTests();
  };

  const fetchTestStats = async (type, id) => {
    try {
      let url;
      switch (type) {
        case 'landing':
          url = `${API_URL}/api/landing-pages/${id}/ab/stats`;
          break;
        case 'banner':
          url = `${API_URL}/api/marketing/banners/${id}/ab/stats`;
          break;
        case 'coupon':
          url = `${API_URL}/api/marketing/coupons/${id}/ab/stats`;
          break;
        default:
          return;
      }
      
      const res = await axios.get(url, { headers });
      setTestStats(res.data);
      setSelectedTest({ type, id });
    } catch (error) {
      toast.error('Failed to fetch test stats');
    }
  };

  // Helper to get single item data
  const getItemData = async (type, id) => {
    try {
      let url;
      switch (type) {
        case 'landing':
          url = `${API_URL}/api/landing-pages/${id}`;
          break;
        case 'banner':
          url = `${API_URL}/api/marketing/banners/${id}`;
          break;
        case 'coupon':
          url = `${API_URL}/api/marketing/coupons/${id}`;
          break;
        default:
          return null;
      }
      const res = await axios.get(url, { headers });
      return res.data;
    } catch {
      return null;
    }
  };

  // Enable A/B testing on an existing item
  const enableABTesting = async (type, id, variants) => {
    try {
      // First fetch existing item data
      const existingData = await getItemData(type, id);
      if (!existingData) {
        toast.error('Failed to fetch item data');
        return;
      }

      let url;
      switch (type) {
        case 'landing':
          url = `${API_URL}/api/landing-pages/${id}`;
          break;
        case 'banner':
          url = `${API_URL}/api/marketing/banners/${id}`;
          break;
        case 'coupon':
          url = `${API_URL}/api/marketing/coupons/${id}`;
          break;
        default:
          return;
      }
      
      // Merge A/B testing fields with existing data
      const updateData = {
        ...existingData,
        ab_testing_enabled: true,
        ab_variants: variants.map((v, idx) => ({
          ...v,
          id: v.id || `variant-${Date.now()}-${idx}`,
          impressions: v.impressions || 0,
          conversions: v.conversions || 0
        }))
      };
      
      // Remove _id if present (MongoDB artifact)
      delete updateData._id;
      
      await axios.put(url, updateData, { headers });
      
      toast.success('A/B testing enabled');
      setShowCreateDialog(false);
      fetchAllTests();
    } catch (error) {
      console.error('Enable A/B testing error:', error);
      toast.error('Failed to enable A/B testing');
    }
  };

  // Toggle A/B testing on/off
  const toggleABTesting = async (type, id, currentState) => {
    try {
      // First fetch existing item data
      const existingData = await getItemData(type, id);
      if (!existingData) {
        toast.error('Failed to fetch item data');
        return;
      }

      let url;
      switch (type) {
        case 'landing':
          url = `${API_URL}/api/landing-pages/${id}`;
          break;
        case 'banner':
          url = `${API_URL}/api/marketing/banners/${id}`;
          break;
        case 'coupon':
          url = `${API_URL}/api/marketing/coupons/${id}`;
          break;
        default:
          return;
      }
      
      // Merge toggle with existing data
      const updateData = {
        ...existingData,
        ab_testing_enabled: !currentState
      };
      delete updateData._id;
      
      await axios.put(url, updateData, { headers });
      toast.success(currentState ? 'A/B testing paused' : 'A/B testing resumed');
      fetchAllTests();
    } catch (error) {
      console.error('Toggle A/B testing error:', error);
      toast.error('Failed to toggle A/B testing');
    }
  };

  // Update variants for an existing test
  const updateVariants = async (type, id, variants) => {
    try {
      // First fetch existing item data
      const existingData = await getItemData(type, id);
      if (!existingData) {
        toast.error('Failed to fetch item data');
        return;
      }

      let url;
      switch (type) {
        case 'landing':
          url = `${API_URL}/api/landing-pages/${id}`;
          break;
        case 'banner':
          url = `${API_URL}/api/marketing/banners/${id}`;
          break;
        case 'coupon':
          url = `${API_URL}/api/marketing/coupons/${id}`;
          break;
        default:
          return;
      }
      
      // Merge variants with existing data
      const updateData = {
        ...existingData,
        ab_variants: variants.map((v, idx) => ({
          ...v,
          id: v.id || `variant-${Date.now()}-${idx}`
        }))
      };
      delete updateData._id;
      
      await axios.put(url, updateData, { headers });
      toast.success('Variants updated');
      setShowManageDialog(false);
      setManageItem(null);
      fetchAllTests();
    } catch (error) {
      console.error('Update variants error:', error);
      toast.error('Failed to update variants');
    }
  };

  const openManageDialog = (type, item) => {
    setManageType(type);
    setManageItem(item);
    setShowManageDialog(true);
  };

  const totalActiveTests = landingPages.length + banners.length + coupons.length;

  if (loading) {
    return (
      <div className="animate-pulse space-y-6" data-testid="ab-testing-dashboard">
        <div className="h-8 bg-gray-200 rounded w-48"></div>
        <div className="grid md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-gray-200 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ab-testing-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">A/B Testing Dashboard</h2>
          <p className="text-gray-600">Create, manage, and monitor all A/B tests</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button className="bg-[#003456]" onClick={() => setShowCreateDialog(true)} data-testid="create-ab-test-btn">
            <Plus className="h-4 w-4 mr-2" />
            Create A/B Test
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <FlaskConical className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Active Tests</p>
              <p className="text-2xl font-bold">{totalActiveTests}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Landing Page Tests</p>
              <p className="text-2xl font-bold">{landingPages.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <Megaphone className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Banner Tests</p>
              <p className="text-2xl font-bold">{banners.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-100 rounded-lg">
              <Gift className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Coupon Tests</p>
              <p className="text-2xl font-bold">{coupons.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 'overview' ? 'border-[#003456] text-[#003456]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          All Tests
        </button>
        <button
          onClick={() => setActiveTab('landing')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 'landing' ? 'border-[#003456] text-[#003456]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Landing Pages ({landingPages.length})
        </button>
        <button
          onClick={() => setActiveTab('banners')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 'banners' ? 'border-[#003456] text-[#003456]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Banners ({banners.length})
        </button>
        <button
          onClick={() => setActiveTab('coupons')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 'coupons' ? 'border-[#003456] text-[#003456]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Coupons ({coupons.length})
        </button>
      </div>

      {/* No Tests Message */}
      {totalActiveTests === 0 && (
        <div className="text-center py-16 bg-gray-50 rounded-xl">
          <FlaskConical className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No Active A/B Tests</h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Start running A/B tests to optimize your marketing campaigns. Click "Create A/B Test" to get started.
          </p>
        </div>
      )}

      {/* Test Cards */}
      {totalActiveTests > 0 && (
        <div className="grid md:grid-cols-2 gap-4">
          {(activeTab === 'overview' || activeTab === 'landing') && landingPages.map((page) => (
            <TestCard
              key={page.id}
              type="landing"
              icon={<FileText className="h-5 w-5" />}
              iconBg="bg-blue-100"
              iconColor="text-blue-600"
              name={page.name}
              description={`/promo/${page.slug}`}
              variants={page.ab_variants || []}
              isActive={page.ab_testing_enabled}
              onViewStats={() => fetchTestStats('landing', page.id)}
              onManage={() => openManageDialog('landing', page)}
              onToggle={() => toggleABTesting('landing', page.id, page.ab_testing_enabled)}
            />
          ))}

          {(activeTab === 'overview' || activeTab === 'banners') && banners.map((banner) => (
            <TestCard
              key={banner.id}
              type="banner"
              icon={<Megaphone className="h-5 w-5" />}
              iconBg="bg-green-100"
              iconColor="text-green-600"
              name={banner.name}
              description={banner.title}
              variants={banner.ab_variants || []}
              isActive={banner.ab_testing_enabled}
              onViewStats={() => fetchTestStats('banner', banner.id)}
              onManage={() => openManageDialog('banner', banner)}
              onToggle={() => toggleABTesting('banner', banner.id, banner.ab_testing_enabled)}
            />
          ))}

          {(activeTab === 'overview' || activeTab === 'coupons') && coupons.map((coupon) => (
            <TestCard
              key={coupon.id}
              type="coupon"
              icon={<Gift className="h-5 w-5" />}
              iconBg="bg-amber-100"
              iconColor="text-amber-600"
              name={coupon.code}
              description={coupon.description}
              variants={coupon.ab_variants || []}
              isActive={coupon.ab_testing_enabled}
              onViewStats={() => fetchTestStats('coupon', coupon.id)}
              onManage={() => openManageDialog('coupon', coupon)}
              onToggle={() => toggleABTesting('coupon', coupon.id, coupon.ab_testing_enabled)}
            />
          ))}
        </div>
      )}

      {/* Stats Modal */}
      {selectedTest && testStats && (
        <Dialog open={!!selectedTest} onOpenChange={() => { setSelectedTest(null); setTestStats(null); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>A/B Test Statistics</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-500">Total Impressions</p>
                  <p className="text-2xl font-bold">{testStats.total_impressions?.toLocaleString() || 0}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-500">Total Conversions</p>
                  <p className="text-2xl font-bold">{testStats.total_conversions?.toLocaleString() || 0}</p>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold">Variant Performance</h4>
                {(testStats.variants || []).map((variant, idx) => {
                  const rate = variant.impressions > 0 ? ((variant.conversions / variant.impressions) * 100).toFixed(2) : 0;
                  const isWinner = testStats.winner_id === variant.id;
                  return (
                    <div 
                      key={variant.id || idx} 
                      className={`p-4 rounded-lg border ${isWinner ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium flex items-center gap-2">
                          {variant.name}
                          {isWinner && <Trophy className="h-4 w-4 text-green-600" />}
                        </span>
                        <span className={`font-bold ${isWinner ? 'text-green-600' : ''}`}>{rate}%</span>
                      </div>
                      <div className="flex gap-4 text-sm text-gray-500">
                        <span>{variant.impressions?.toLocaleString() || 0} impressions</span>
                        <span>{variant.conversions?.toLocaleString() || 0} conversions</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {testStats.statistical_significance !== undefined && (
                <div className={`p-3 rounded-lg ${testStats.statistical_significance ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                  <p className="text-sm font-medium">
                    {testStats.statistical_significance 
                      ? '✓ Results are statistically significant' 
                      : '⏳ More data needed for statistical significance'}
                  </p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Create A/B Test Dialog */}
      <CreateABTestDialog 
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onEnable={enableABTesting}
        landingPages={allLandingPages.filter(p => !p.ab_testing_enabled)}
        banners={allBanners.filter(b => !b.ab_testing_enabled)}
        coupons={allCoupons.filter(c => !c.ab_testing_enabled)}
      />

      {/* Manage Variants Dialog */}
      <ManageVariantsDialog
        open={showManageDialog}
        onClose={() => { setShowManageDialog(false); setManageItem(null); }}
        type={manageType}
        item={manageItem}
        onSave={(variants) => updateVariants(manageType, manageItem?.id, variants)}
      />
    </div>
  );
};

// Create A/B Test Dialog Component
const CreateABTestDialog = ({ open, onClose, onEnable, landingPages, banners, coupons }) => {
  const [testType, setTestType] = useState('landing');
  const [selectedItem, setSelectedItem] = useState('');
  const [variants, setVariants] = useState([
    { name: 'Control', headline: '', description: '' },
    { name: 'Variant B', headline: '', description: '' }
  ]);

  const addVariant = () => {
    setVariants([...variants, { 
      name: `Variant ${String.fromCharCode(65 + variants.length)}`, 
      headline: '', 
      description: '' 
    }]);
  };

  const removeVariant = (idx) => {
    if (variants.length > 2) {
      setVariants(variants.filter((_, i) => i !== idx));
    }
  };

  const updateVariant = (idx, field, value) => {
    const updated = [...variants];
    updated[idx][field] = value;
    setVariants(updated);
  };

  const handleCreate = () => {
    if (!selectedItem) {
      toast.error('Please select an item to test');
      return;
    }
    if (variants.some(v => !v.name.trim())) {
      toast.error('All variants must have a name');
      return;
    }
    onEnable(testType, selectedItem, variants);
  };

  const getItems = () => {
    switch (testType) {
      case 'landing': return landingPages;
      case 'banner': return banners;
      case 'coupon': return coupons;
      default: return [];
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5 text-purple-600" />
            Create A/B Test
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6 pt-4">
          {/* Test Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Test Type</label>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => { setTestType('landing'); setSelectedItem(''); }}
                className={`p-4 rounded-lg border-2 transition-colors ${testType === 'landing' ? 'border-[#003456] bg-[#003456]/5' : 'border-gray-200 hover:border-gray-300'}`}
              >
                <FileText className={`h-6 w-6 mx-auto mb-2 ${testType === 'landing' ? 'text-[#003456]' : 'text-gray-400'}`} />
                <p className="text-sm font-medium">Landing Page</p>
                <p className="text-xs text-gray-500">{landingPages.length} available</p>
              </button>
              <button
                type="button"
                onClick={() => { setTestType('banner'); setSelectedItem(''); }}
                className={`p-4 rounded-lg border-2 transition-colors ${testType === 'banner' ? 'border-[#003456] bg-[#003456]/5' : 'border-gray-200 hover:border-gray-300'}`}
              >
                <Megaphone className={`h-6 w-6 mx-auto mb-2 ${testType === 'banner' ? 'text-[#003456]' : 'text-gray-400'}`} />
                <p className="text-sm font-medium">Banner</p>
                <p className="text-xs text-gray-500">{banners.length} available</p>
              </button>
              <button
                type="button"
                onClick={() => { setTestType('coupon'); setSelectedItem(''); }}
                className={`p-4 rounded-lg border-2 transition-colors ${testType === 'coupon' ? 'border-[#003456] bg-[#003456]/5' : 'border-gray-200 hover:border-gray-300'}`}
              >
                <Gift className={`h-6 w-6 mx-auto mb-2 ${testType === 'coupon' ? 'text-[#003456]' : 'text-gray-400'}`} />
                <p className="text-sm font-medium">Coupon</p>
                <p className="text-xs text-gray-500">{coupons.length} available</p>
              </button>
            </div>
          </div>

          {/* Item Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Item to Test</label>
            {getItems().length === 0 ? (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm">
                No {testType === 'landing' ? 'landing pages' : testType === 'banner' ? 'banners' : 'coupons'} available for A/B testing. 
                All existing items already have A/B testing enabled.
              </div>
            ) : (
              <select 
                value={selectedItem} 
                onChange={(e) => setSelectedItem(e.target.value)}
                className="w-full p-3 border rounded-lg"
                data-testid="select-test-item"
              >
                <option value="">Choose {testType}...</option>
                {getItems().map(item => (
                  <option key={item.id} value={item.id}>
                    {testType === 'landing' ? item.name : testType === 'banner' ? item.name || item.title : item.code}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Variants Configuration */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-gray-700">Test Variants</label>
              <Button type="button" variant="outline" size="sm" onClick={addVariant}>
                <Plus className="h-4 w-4 mr-1" /> Add Variant
              </Button>
            </div>
            <div className="space-y-3">
              {variants.map((variant, idx) => (
                <div key={idx} className="p-4 border rounded-lg bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${idx === 0 ? 'bg-blue-500 text-white' : 'bg-purple-500 text-white'}`}>
                        {String.fromCharCode(65 + idx)}
                      </span>
                      <Input
                        value={variant.name}
                        onChange={(e) => updateVariant(idx, 'name', e.target.value)}
                        className="w-40"
                        placeholder="Variant name"
                      />
                    </div>
                    {variants.length > 2 && (
                      <button 
                        type="button"
                        onClick={() => removeVariant(idx)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <Input
                      value={variant.headline || ''}
                      onChange={(e) => updateVariant(idx, 'headline', e.target.value)}
                      placeholder="Headline variation"
                    />
                    <Input
                      value={variant.description || ''}
                      onChange={(e) => updateVariant(idx, 'description', e.target.value)}
                      placeholder="Description variation"
                    />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Add at least 2 variants. The system will randomly show one variant to each visitor and track conversions.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-[#003456]" data-testid="start-ab-test-btn">
              <FlaskConical className="h-4 w-4 mr-2" />
              Start A/B Test
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Manage Variants Dialog Component
const ManageVariantsDialog = ({ open, onClose, type, item, onSave }) => {
  const [variants, setVariants] = useState([]);

  useEffect(() => {
    if (item) {
      setVariants(item.ab_variants || []);
    }
  }, [item]);

  const addVariant = () => {
    setVariants([...variants, { 
      name: `Variant ${String.fromCharCode(65 + variants.length)}`, 
      headline: '', 
      description: '',
      impressions: 0,
      conversions: 0
    }]);
  };

  const removeVariant = (idx) => {
    if (variants.length > 1) {
      setVariants(variants.filter((_, i) => i !== idx));
    }
  };

  const updateVariant = (idx, field, value) => {
    const updated = [...variants];
    updated[idx][field] = value;
    setVariants(updated);
  };

  const resetStats = (idx) => {
    const updated = [...variants];
    updated[idx].impressions = 0;
    updated[idx].conversions = 0;
    setVariants(updated);
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-gray-600" />
            Manage Test Variants
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm text-gray-600">
              <strong>{type === 'landing' ? 'Landing Page' : type === 'banner' ? 'Banner' : 'Coupon'}:</strong>{' '}
              {type === 'landing' ? item.name : type === 'banner' ? item.name || item.title : item.code}
            </p>
          </div>

          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">Variants ({variants.length})</label>
            <Button type="button" variant="outline" size="sm" onClick={addVariant}>
              <Plus className="h-4 w-4 mr-1" /> Add Variant
            </Button>
          </div>

          <div className="space-y-3">
            {variants.map((variant, idx) => {
              const rate = variant.impressions > 0 ? ((variant.conversions || 0) / variant.impressions * 100).toFixed(1) : 0;
              return (
                <div key={variant.id || idx} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${idx === 0 ? 'bg-blue-500 text-white' : 'bg-purple-500 text-white'}`}>
                        {String.fromCharCode(65 + idx)}
                      </span>
                      <Input
                        value={variant.name}
                        onChange={(e) => updateVariant(idx, 'name', e.target.value)}
                        className="w-40"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">
                        {variant.impressions || 0} views • {rate}%
                      </span>
                      <button 
                        type="button"
                        onClick={() => resetStats(idx)}
                        className="text-gray-400 hover:text-gray-600 p-1"
                        title="Reset stats"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </button>
                      {variants.length > 1 && (
                        <button 
                          type="button"
                          onClick={() => removeVariant(idx)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <Input
                      value={variant.headline || ''}
                      onChange={(e) => updateVariant(idx, 'headline', e.target.value)}
                      placeholder="Headline variation"
                    />
                    <Input
                      value={variant.description || ''}
                      onChange={(e) => updateVariant(idx, 'description', e.target.value)}
                      placeholder="Description variation"
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={() => onSave(variants)} className="bg-[#003456]">
              <Check className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ABTestingDashboard;
